import React from 'react';
// @ts-ignore - Remotion型定義の互換性問題の暫定対応
import { AbsoluteFill, useCurrentFrame, interpolate, spring, useVideoConfig } from 'remotion';

export interface UgcStyleProps {
  username: string;
  reviewText: string;
  productName: string;
  rating: number;
  ctaText: string;
  primaryColor: string;
  aspectRatio: '1:1' | '16:9' | '9:16';
}

export const UgcStyle: React.FC<UgcStyleProps> = ({
  username,
  reviewText,
  productName,
  rating,
  ctaText,
  primaryColor,
  aspectRatio,
}) => {
  const frame = useCurrentFrame();
  const { fps, durationInFrames } = useVideoConfig();
  const isPortrait = aspectRatio === '9:16';

  // Background gradient shift
  const bgShift = interpolate(frame, [0, durationInFrames], [0, 30]);

  // Avatar pop-in
  const avatarScale = spring({
    frame: frame - 5,
    fps,
    config: { damping: 8, mass: 0.6 },
  });

  // Username slide in
  const usernameOpacity = interpolate(frame, [15, 30], [0, 1], {
    extrapolateRight: 'clamp',
  });
  const usernameX = interpolate(frame, [15, 30], [-30, 0], {
    extrapolateRight: 'clamp',
  });

  // Stars animation
  const getStarAnim = (index: number) => {
    const start = 35 + index * 8;
    return spring({
      frame: frame - start,
      fps,
      config: { damping: 6, mass: 0.3 },
    });
  };

  // Review text typewriter effect
  const charsToShow = Math.floor(
    interpolate(frame, [70, 70 + reviewText.length * 1.5], [0, reviewText.length], {
      extrapolateRight: 'clamp',
    })
  );

  // Product name slide up
  const productStart = 70 + reviewText.length * 1.5 + 20;
  const productOpacity = interpolate(frame, [productStart, productStart + 20], [0, 1], {
    extrapolateRight: 'clamp',
  });
  const productY = interpolate(frame, [productStart, productStart + 20], [20, 0], {
    extrapolateRight: 'clamp',
  });

  // CTA animation
  const ctaStart = productStart + 40;
  const ctaScale = spring({
    frame: frame - ctaStart,
    fps,
    config: { damping: 8, mass: 0.5 },
  });
  const ctaOpacity = interpolate(frame, [ctaStart, ctaStart + 15], [0, 1], {
    extrapolateRight: 'clamp',
  });

  // Star rendering helper
  const renderStars = () => {
    return Array.from({ length: 5 }, (_, i) => {
      const scale = i < rating ? getStarAnim(i) : 0;
      const filled = i < rating;
      return (
        <span
          key={i}
          style={{
            fontSize: isPortrait ? '28px' : '32px',
            transform: `scale(${scale})`,
            display: 'inline-block',
            marginRight: '4px',
            filter: filled ? 'none' : 'grayscale(1) opacity(0.3)',
          }}
        >
          {filled ? '\u2B50' : '\u2606'}
        </span>
      );
    });
  };

  return (
    <AbsoluteFill
      style={{
        background: `linear-gradient(${160 + bgShift}deg, #f8f4f0 0%, #fff5eb 50%, #fef3e8 100%)`,
        fontFamily: "'Hiragino Sans', 'Noto Sans JP', sans-serif",
        overflow: 'hidden',
      }}
    >
      {/* Subtle pattern overlay */}
      <div
        style={{
          position: 'absolute',
          inset: 0,
          backgroundImage: 'radial-gradient(circle at 25% 25%, rgba(0,0,0,0.02) 1px, transparent 1px)',
          backgroundSize: '20px 20px',
        }}
      />

      {/* Main content */}
      <AbsoluteFill
        style={{
          justifyContent: isPortrait ? 'center' : 'center',
          alignItems: 'center',
          padding: isPortrait ? '60px 24px' : '40px 80px',
        }}
      >
        {/* User info row */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            marginBottom: '24px',
            gap: '16px',
          }}
        >
          {/* Avatar circle */}
          <div
            style={{
              width: isPortrait ? '56px' : '64px',
              height: isPortrait ? '56px' : '64px',
              borderRadius: '50%',
              background: `linear-gradient(135deg, ${primaryColor}, #ff6b9d)`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              transform: `scale(${avatarScale})`,
              boxShadow: '0 4px 15px rgba(0,0,0,0.1)',
            }}
          >
            <span style={{ fontSize: isPortrait ? '24px' : '28px', color: '#fff', fontWeight: 700 }}>
              {username.charAt(0).toUpperCase()}
            </span>
          </div>

          {/* Username */}
          <div
            style={{
              opacity: usernameOpacity,
              transform: `translateX(${usernameX}px)`,
            }}
          >
            <div style={{ fontSize: isPortrait ? '18px' : '22px', fontWeight: 700, color: '#333' }}>
              {username}
            </div>
            <div style={{ fontSize: isPortrait ? '13px' : '14px', color: '#999' }}>
              verified purchase
            </div>
          </div>
        </div>

        {/* Star rating */}
        <div style={{ marginBottom: '24px' }}>
          {renderStars()}
        </div>

        {/* Review text with typewriter */}
        <div
          style={{
            maxWidth: isPortrait ? '100%' : '700px',
            marginBottom: '32px',
            minHeight: isPortrait ? '100px' : '80px',
          }}
        >
          <p
            style={{
              fontSize: isPortrait ? '22px' : '26px',
              color: '#333',
              lineHeight: 1.6,
              textAlign: 'center',
              fontWeight: 500,
            }}
          >
            &ldquo;{reviewText.substring(0, charsToShow)}&rdquo;
          </p>
        </div>

        {/* Product name */}
        <div
          style={{
            opacity: productOpacity,
            transform: `translateY(${productY}px)`,
            marginBottom: '32px',
          }}
        >
          <div
            style={{
              display: 'inline-block',
              background: 'rgba(0,0,0,0.05)',
              padding: isPortrait ? '8px 20px' : '10px 28px',
              borderRadius: '20px',
            }}
          >
            <span
              style={{
                fontSize: isPortrait ? '16px' : '18px',
                color: '#666',
                fontWeight: 500,
              }}
            >
              {productName}
            </span>
          </div>
        </div>

        {/* CTA */}
        <div
          style={{
            opacity: ctaOpacity,
            transform: `scale(${ctaScale})`,
          }}
        >
          <div
            style={{
              background: primaryColor,
              color: '#ffffff',
              padding: isPortrait ? '14px 40px' : '16px 48px',
              borderRadius: '50px',
              fontSize: isPortrait ? '20px' : '24px',
              fontWeight: 700,
              boxShadow: `0 6px 20px ${primaryColor}44`,
            }}
          >
            {ctaText}
          </div>
        </div>
      </AbsoluteFill>

      {/* Bottom progress */}
      <div
        style={{
          position: 'absolute',
          bottom: 0,
          left: 0,
          height: '3px',
          width: `${interpolate(frame, [0, durationInFrames], [0, 100])}%`,
          background: primaryColor,
          opacity: 0.5,
        }}
      />
    </AbsoluteFill>
  );
};
